import { Link } from "react-router-dom";
import { SITE } from "../config";
import { usePageMeta } from "../seo";
import LegalLayout from "../components/LegalLayout";

const UPDATED = "Effective 20 October 2026";

export default function Privacy() {
  usePageMeta("/privacy");
  const mail = (
    <a href={`mailto:${SITE.contactEmail}?subject=Privacy`}>{SITE.contactEmail}</a>
  );
  return (
    <LegalLayout title="Privacy policy" updated={UPDATED}>
      <section>
        <p>
          This policy explains what data {SITE.productName} processes, why, where
          it's kept and who else sees it. {SITE.productName} is a helpdesk that
          businesses ("organizations") use to answer their customers. It's
          operated by {SITE.productName}, Bangalore, India ("we").
        </p>
        <p>
          There are two kinds of data. Data about the people who use{" "}
          {SITE.productName} at an organization (agents) is ours to look after.
          Data an organization brings in about its own customers, such as their
          messages, belongs to that organization: we process it on the
          organization's behalf and follow its instructions.
        </p>
      </section>

      <section>
        <h2>What we process</h2>
        <ul>
          <li>
            <strong>Agents:</strong> when you sign in with Google, your name,
            email address, profile picture and Google account id. Also the
            organization you belong to, your role, and what you do in{" "}
            {SITE.productName} (for example, replies you send and notes you
            write).
          </li>
          <li>
            <strong>Organizations' customers:</strong> the messages, emails,
            WhatsApp messages and call details an organization's channels bring
            in, the replies sent to them, their contact details (name, email
            address, phone number), any fields or companies the organization
            adds, and their answers to satisfaction surveys.
          </li>
          <li>
            <strong>Attachments:</strong> files that arrive with messages and
            files agents attach, up to 10 MB each. For phone calls through
            Acefone we store a link to the recording, which stays with Acefone.
          </li>
          <li>
            <strong>Connection details:</strong> the credentials an organization
            gives us to connect its mailboxes, WhatsApp number, Acefone account,
            store and AI provider. These are encrypted before they're stored.
          </li>
          <li>
            <strong>Usage records:</strong> for AI features, how many requests
            and tokens an organization used (not the text sent or received),
            and ordinary server logs used to run and fix the service.
          </li>
        </ul>
        <p>
          This website doesn't use analytics or advertising cookies. The{" "}
          {SITE.productName} app keeps your sign-in token in your browser's
          local storage so you stay signed in.
        </p>
      </section>

      <section>
        <h2>Why we process it</h2>
        <p>
          To provide {SITE.productName}: to sign you in, receive and show
          messages, send replies, show customer and store details on tickets,
          run the rules, surveys and reports an organization sets up, and keep
          the service secure and working. We don't sell personal data, and we
          don't use it for advertising.
        </p>
      </section>

      <section>
        <h2>Where it's stored</h2>
        <p>
          {SITE.productName} runs on Google Cloud in the United States. Our
          databases and stored attachments are there.
        </p>
      </section>

      <section>
        <h2>Who else receives it</h2>
        <ul>
          <li>
            <strong>Google Cloud</strong> hosts the service.
          </li>
          <li>
            <strong>Services an organization connects.</strong> Replies are
            sent through the organization's own mailbox or mail server,
            WhatsApp messages through its Meta account, and calls through its
            Acefone account. Store details are fetched from its Shopify or
            Zopping store, and data is sent to the endpoints it configures
            (webhooks, customer data lookup, ticket actions).
          </li>
          <li>
            <strong>AI providers, only if the organization turns them on.</strong>{" "}
            When an agent asks for a draft or a summary, the ticket's
            conversation is sent to OpenAI or Anthropic using the
            organization's own API key, under the organization's own agreement
            with that provider. We have no AI account of our own.
          </li>
          <li>
            When the law requires it, for example a valid court order.
          </li>
        </ul>
      </section>

      <section>
        <h2>How long we keep it</h2>
        <p>
          We keep an organization's data while it uses {SITE.productName}.
          Records of webhook deliveries are kept for 7 days. To have your data
          or your organization's data deleted, email {mail}; we'll confirm once
          it's done.
        </p>
      </section>

      <section>
        <h2>Your choices and rights</h2>
        <p>
          You can ask us for a copy of the personal data we hold about you, or
          ask us to correct or delete it, by emailing {mail}. If you're the
          customer of a business that uses {SITE.productName}, the business
          decides what happens to your conversations, so please contact them
          first; we'll help them with your request.
        </p>
        <p>
          How we protect data is described on the{" "}
          <Link to="/security">security page</Link>.
        </p>
      </section>

      <section>
        <h2>Changes</h2>
        <p>
          {SITE.productName} is in beta and this policy will change as the
          product does. When it changes we'll update this page and the date at
          the top.
        </p>
      </section>

      <section>
        <h2>Contact</h2>
        <p>
          Questions about privacy: {mail}. {SITE.productName}, Bangalore,
          India.
        </p>
      </section>
    </LegalLayout>
  );
}
